import { Component, ViewEncapsulation, ChangeDetectionStrategy, forwardRef, Input, Injector, OnInit } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { combineLatest, Observable } from 'rxjs';
import { ContactSummary, ContractorRecommendedResourcesRequest, UnallocatedUser } from '@jms/model';
import { PickerBaseComponent } from '@jms/ui/ui.pickers.base';
import { ObservableInput } from '@jms/utils/util.redux';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import * as Enumerable from 'linq';
import { PortalContactDataService } from '../../services/portal.contact.data.service';
import { applyRecommendationGrouping } from '../../utilities/contact.grouping.utility';

@Component({
    selector: 'contractor-allocation-employee-picker',
    templateUrl: './contractor.allocation.employee.picker.component.html',
    styleUrls: ['./contractor.allocation.employee.picker.component.less'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => ContractorAllocationEmployeePickerComponent),
        }],
    standalone: false
})
export class ContractorAllocationEmployeePickerComponent extends PickerBaseComponent implements OnInit {

  @Input() public placeholder = '<Resource>';
  @Input() public closeOnSelect = true;
  @Input() public dropdownPosition: 'auto' | 'bottom' | 'top' = 'auto';

  @ObservableInput() @Input('parentId') public parentId$: Observable<string>;
  @ObservableInput() @Input('request') public request$: Observable<Omit<ContractorRecommendedResourcesRequest, 'parentId'>>;
  @ObservableInput() @Input('exclude') public exclude$: Observable<string[]>;
  @ObservableInput() @Input('unassigned') public unassigned$: Observable<boolean>;


  public data$: Observable<ContactSummary[]>;

  constructor(
    injector: Injector,
    private dataService: PortalContactDataService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    const dataList$ = combineLatest([this.parentId$, this.request$])
      .pipe(
        // Callers rebuild the request object on every change detection, so only refetch when its content actually changes.
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        switchMap(([parentId, request]) => {
          return this.dataService.getRecommendedContractorEmployees({ ...request, parentId });
        })
      );

    this.data$ = combineLatest([dataList$, this.exclude$, this.unassigned$])
      .pipe(
        map(([dataList, exclude, unassigned]) => {

          dataList = unassigned ? [UnallocatedUser, ...dataList] : dataList;

          const items = Enumerable.from(dataList)
            .where(e => {
              return exclude && exclude.length
                ? Enumerable.from(exclude).all(i => e?._id !== i)
                : true;
            })
            .toArray();

          return applyRecommendationGrouping(items, true);
        })
      );
  }


}
